/**
 * CRA Article 14 domain logic for the console.
 *
 * Everything the report/incident editor needs to be legally correct lives here,
 * with the REVIEW-FIXES baked in:
 *   C-1  every deadline anchors to a legal T0 (aware-since), not stage entry.
 *   C-2  vulnerabilities and severe incidents have SEPARATE stage tracks.
 *   C-5  the CSIRT list is the 27 national coordinators (no GovCERT.ch / CERT-EU).
 *   C-9  CSAF wording softened ("JSON aligned with CSAF 2.0").
 *   U-1  manual report statuses: New · Triaged · Reviewed · Sent (Pending review = Generate).
 */
import type { IncidentKind, ReportStageKey, ReportStatus } from './contract';

const HOUR = 3600e3;
const DAY = 86400e3;

export interface DeadlineCtx {
  /** aware-since (KEV match / detection) — drives 24h & 72h (Art.14(2)(a)(b)). */
  t0: number;
  /** Q16 v21 — corrective measure available; anchors the 14d final (Art.14(2)(c)). */
  fixAvailableAt: number | null;
  /** For severe incidents: when the 72h notification was sent; anchors +1 month. */
  notified72At: number | null;
}

export interface StageDef {
  key: ReportStageKey;
  label: string;
  cap: string;
  /** shown (no digits) when the deadline anchor has not started yet. */
  capWaiting?: string;
  slug: string;
  /** ms epoch of the deadline, or null when its legal anchor hasn't started. */
  deadline: (c: DeadlineCtx) => number | null;
}

// ── Vulnerability track (incl. KEV-triggered): 24h → 72h → Final ≤14d after fix ──
export const VULN_STAGES: StageDef[] = [
  { key: '24h', label: '24h · early warning', slug: '24h-early-warning',
    cap: 'until ENISA early-warning deadline', deadline: (c) => c.t0 + 24 * HOUR },
  { key: '72h', label: '72h · detailed notification', slug: '72h-notification',
    cap: 'until 72h detailed-notification deadline', deadline: (c) => c.t0 + 72 * HOUR },
  { key: '14d', label: '14d · final report', slug: '14d-final',
    cap: 'until 14-day final-report deadline',
    capWaiting: 'awaiting corrective measure — the 14-day clock starts when it ships',
    deadline: (c) => (c.fixAvailableAt ? c.fixAvailableAt + 14 * DAY : null) },
  { key: 'complete', label: 'completed', slug: 'final', cap: 'obligation complete', deadline: () => null },
];

// ── Severe-incident track: 24h → 72h → Final ≤1 month after the 72h notification ──
export const INCIDENT_STAGES: StageDef[] = [
  { key: '24h', label: '24h · early warning', slug: '24h-early-warning',
    cap: 'until ENISA early-warning deadline', deadline: (c) => c.t0 + 24 * HOUR },
  { key: '72h', label: '72h · incident notification', slug: '72h-notification',
    cap: 'until 72h incident-notification deadline', deadline: (c) => c.t0 + 72 * HOUR },
  { key: '1m', label: '1 month · final report', slug: 'final',
    cap: 'until 1-month final-report deadline',
    capWaiting: 'the 1-month clock starts when the 72h notification is sent',
    deadline: (c) => (c.notified72At ? c.notified72At + 30 * DAY : null) },
  { key: 'complete', label: 'completed', slug: 'final', cap: 'obligation complete', deadline: () => null },
];

export function stagesFor(kind: IncidentKind): StageDef[] {
  return kind === 'incident' ? INCIDENT_STAGES : VULN_STAGES;
}

/** hh:mm:ss until a deadline (or a phrase when the anchor hasn't started / passed). */
export function remainingLabel(deadlineMs: number | null, now: number): { text: string; overdue: boolean; waiting: boolean } {
  if (deadlineMs === null) return { text: '—', overdue: false, waiting: true };
  const s = Math.floor((deadlineMs - now) / 1000);
  if (s <= 0) return { text: 'overdue', overdue: true, waiting: false };
  const two = (n: number) => String(n).padStart(2, '0');
  const d = Math.floor(s / 86400);
  const hh = Math.floor((s % 86400) / 3600);
  const text = d > 0 ? `${d}d ${two(hh)}h` : `${two(Math.floor(s / 3600))}:${two(Math.floor((s % 3600) / 60))}:${two(s % 60)}`;
  return { text, overdue: false, waiting: false };
}

// ── Report status (U-1) — manual picks only; Pending review is set by Generate ──
export const MANUAL_STATUSES: ReportStatus[] = ['New', 'Triaged', 'Reviewed', 'Sent'];
export const ALL_STATUSES: ReportStatus[] = ['New', 'Triaged', 'Pending review', 'Reviewed', 'Sent'];

// ── VEX / CISA justifications (5, no default) — src/services vex-justification.ts ──
export const CISA_JUSTIFICATIONS: Array<{ code: string; label: string }> = [
  { code: 'component_not_present', label: "the package isn't shipped at all" },
  { code: 'vulnerable_code_not_present', label: "the vulnerable code isn't in our build" },
  { code: 'vulnerable_code_not_in_execute_path', label: 'present but never on the execute path' },
  { code: 'vulnerable_code_cannot_be_controlled_by_adversary', label: "an attacker can't drive it" },
  { code: 'inline_mitigations_already_exist', label: 'an existing control blocks exploitation' },
];

export const VEX_STATUSES: Array<{ value: string; label: string }> = [
  { value: 'not_affected', label: 'Not affected' },
  { value: 'affected', label: 'Affected' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'under_investigation', label: 'Under investigation' },
];

// ── National coordinator CSIRTs (REVIEW-FIXES C-5: 27, no GovCERT.ch / CERT-EU) ──
export const CSIRTS: Array<{ n: string; c: string }> = [
  { n: 'CERT-Bund', c: 'DE' }, { n: 'CERT-FR', c: 'FR' }, { n: 'INCIBE-CERT', c: 'ES' },
  { n: 'CSIRT Italia', c: 'IT' }, { n: 'NCSC-NL', c: 'NL' }, { n: 'CERT.be', c: 'BE' },
  { n: 'CERT.at', c: 'AT' }, { n: 'CSIRT.PT', c: 'PT' }, { n: 'NCSC-FI', c: 'FI' },
  { n: 'CERT-SE', c: 'SE' }, { n: 'CFCS', c: 'DK' }, { n: 'GovCERT.CZ', c: 'CZ' },
  { n: 'CSIRT.SK', c: 'SK' }, { n: 'SI-CERT', c: 'SI' }, { n: 'CERT-RO', c: 'RO' },
  { n: 'CERT Polska / NASK', c: 'PL' }, { n: 'GRNET CERT', c: 'GR' }, { n: 'NCSC-IE', c: 'IE' },
  { n: 'CERT.LV', c: 'LV' }, { n: 'NKSC / CERT-LT', c: 'LT' }, { n: 'CERT-EE (RIA)', c: 'EE' },
  { n: 'CIRCL', c: 'LU' }, { n: 'CSIRTMalta', c: 'MT' }, { n: 'CSIRT-CY', c: 'CY' },
  { n: 'CERT Bulgaria', c: 'BG' }, { n: 'ZSIS / CERT.hr', c: 'HR' }, { n: 'CERT.hu', c: 'HU' },
];
